// src/pages/spot/components/TopRankSection.js
// POST/STORE/LAND 탭으로 구성된 상위 유저 표시 컴포넌트

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getEnvironment } from '../../../utils/environment';
import SPOT_MESSAGES from '../constants/SpotMessages';

const TopRankSection = ({ spots, landmarkStatus }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('STORE');
    const [topUser, setTopUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchTopUser = async () => {
            if (activeTab === 'LAND') {
                setLoading(true);
                try {
                    // 현재 표시된 스팟 중 랜드마크만 필터링
                    const landmarks = spots.filter(s => s.type === 'landmark');

                    // 소유자별 랜드 수 및 SPOT 집계
                    const ownerStats = {};

                    landmarks.forEach(land => {
                        const status = landmarkStatus[land.id];
                        if (status?.ownerId) {
                            if (!ownerStats[status.ownerId]) {
                                ownerStats[status.ownerId] = {
                                    landCount: 0,
                                    ownerName: status.ownerName
                                };
                            }
                            ownerStats[status.ownerId].landCount += 1;
                        }
                    });

                    // 1순위: 랜드 수로 정렬
                    const sorted = Object.entries(ownerStats)
                        .map(([ownerId, stats]) => ({
                            ownerId,
                            ...stats
                        }))
                        .sort((a, b) => b.landCount - a.landCount);

                    if (sorted.length > 0) {
                        const topOwnerId = sorted[0].ownerId;

                        // Firestore에서 SPOT 포인트 조회
                        const env = getEnvironment();
                        const usersCollection = `${env === 'production' ? '' : env + '_'}users`;

                        // 🎯 [Fix] Modular SDK syntax
                        const userDoc = await getDoc(doc(db, usersCollection, topOwnerId));

                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            setTopUser({
                                name: sorted[0].ownerName || userData.username || 'Unknown',
                                landCount: sorted[0].landCount,
                                spotPoints: userData.spotPoints || 0
                            });
                        } else {
                            setTopUser({
                                name: sorted[0].ownerName || 'Unknown',
                                landCount: sorted[0].landCount,
                                spotPoints: 0
                            });
                        }
                    } else {
                        setTopUser(null);
                    }
                } catch (error) {
                    console.error('[TopRankSection] Error fetching top user:', error);
                    setTopUser(null);
                } finally {
                    setLoading(false);
                }
            } else {
                // POST/STORE는 기존 로직 (좋아요/댓글 기준)
                const filtered = spots.filter(s =>
                    activeTab === 'POST' ? s.type === 'footprint' : s.type === 'store'
                );

                if (filtered.length > 0) {
                    // 좋아요 + 댓글 수로 정렬
                    const sorted = [...filtered].sort((a, b) => {
                        const scoreA = (a.likeCount || 0) + (a.commentCount || 0);
                        const scoreB = (b.likeCount || 0) + (b.commentCount || 0);
                        return scoreB - scoreA;
                    });

                    setTopUser({
                        name: sorted[0].userName || 'Unknown',
                        type: activeTab,
                        likes: sorted[0].likeCount || 0,
                        comments: sorted[0].commentCount || 0
                    });
                } else {
                    setTopUser(null);
                }
            }
        };

        fetchTopUser();
    }, [activeTab, spots, landmarkStatus]);

    return (
        <Container>
            <TabContainer>
                {['STORE', 'LAND', 'POST'].map(tab => (
                    <Tab
                        key={tab}
                        active={activeTab === tab}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </Tab>
                ))}
            </TabContainer>

            {loading ? (
                <LoadingText>Loading...</LoadingText>
            ) : topUser ? (
                <TopUserDisplay>
                    <Crown>👑</Crown>
                    <UserName>{topUser.name}</UserName>
                    {activeTab === 'LAND' ? (
                        <Stats>
                            {topUser.landCount}개 보유 · {topUser.spotPoints.toFixed(1)} SPOT
                        </Stats>
                    ) : (
                        <Stats>
                            ❤️ {topUser.likes} · 💬 {topUser.comments}
                        </Stats>
                    )}
                </TopUserDisplay>
            ) : (
                <EmptyText>
                    {activeTab === 'LAND' ? t(SPOT_MESSAGES.TOP5_EMPTY_LANDMARKS) : t(SPOT_MESSAGES.EMPTY_DATA)}
                </EmptyText>
            )}
        </Container>
    );
};

const Container = styled.div`
  margin: 16px 16px 8px 16px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const TabContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: ${p => p.active
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'};
  color: ${p => p.active ? 'white' : '#666'};
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${p => p.active ? '0 2px 6px rgba(108, 92, 231, 0.3)' : 'none'};
  
  &:hover {
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TopUserDisplay = styled.div`
  text-align: center;
  padding: 12px 8px;
`;

const Crown = styled.div`
  font-size: 2.5rem;
  margin-bottom: 4px;
`;

const UserName = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: #6C5CE7;
  margin: 6px 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Stats = styled.div`
  font-size: 0.7rem;
  color: #999;
  font-weight: 500;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #999;
  font-size: 0.75rem;
  padding: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  color: #ccc;
  font-size: 0.7rem;
  padding: 16px;
`;

export default TopRankSection;
